import {
  AccountsService,
  TeamAcceptInviteResponse,
} from 'base/accounts_service';
import { Button } from 'base/ui/button';
import { useDocumentTitle } from 'base/ui/document_title';
import { goToDashboard } from 'base/ui/error_dialogs';
import { formatError, RedErrorText, useSubmit } from 'base/ui/form';
import { Rows } from 'base/ui/layout';
import { LoadingDots } from 'base/ui/loading_dots';
import { TextRegular, TextRegularLight, Title5 } from 'base/ui/typography';
import { AuthLayout, AuthNavbar } from 'blocks/auth/layout';
import {
  authRouteToUrl,
  getCurrentLocationForRedirect,
} from 'blocks/auth/routes';
import { dashboardRouteToUrl } from 'blocks/dashboard/routes';
import { manageAccountRouteToUrl } from 'blocks/manage_account/routes';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { logOutWithoutReload } from '../../../library/src/modules/cookies';
import { AcceptTeamInviteRoute } from './routes';

export const AcceptTeamInvitePage = observer(function _AcceptTeamInvitePage({
  route,
  accountsService,
}: {
  route: AcceptTeamInviteRoute;
  accountsService: AccountsService;
}) {
  useDocumentTitle(`Accept Team Invite - Relume`);

  const [res, setRes] = React.useState<TeamAcceptInviteResponse | undefined>();
  const [error, setError] = React.useState<unknown>();
  const [finished, setFinished] = React.useState(false);

  React.useEffect(() => {
    accountsService
      .teamAcceptInvite({ token: route.token, dryRun: true })
      .then(setRes)
      .catch(setError);
  }, [accountsService, route.token]);

  return (
    <>
      <AuthNavbar />
      <AuthLayout
        transitionKey={route.path}
        showQuote={false}
        contentFullWidth={false}
        isFinishing={finished}
      >
        {error ? (
          <ErrorContent error={error} />
        ) : !res ? (
          <LoadingDots aspectRatio={3 / 1} />
        ) : res.status === 'wrongEmail' ? (
          <WrongEmailForm res={res} />
        ) : (
          <AcceptForm
            token={route.token}
            accountsService={accountsService}
            dryRunRes={res}
            onFinished={() => setFinished(true)}
          />
        )}
      </AuthLayout>
    </>
  );
});

function ErrorContent({ error }: { error: unknown }) {
  const errorString = formatError(error);
  const showManageTeam = errorString.includes(
    'you are currently the only Admin of an existing team',
  );
  return (
    <Rows spacing={24}>
      <Rows spacing={16}>
        <Title5>Unable to join team</Title5>
        <TextRegularLight>{errorString}</TextRegularLight>
      </Rows>
      <Rows spacing={8}>
        {showManageTeam && (
          <Button
            label="Manage team"
            size="large"
            variant="primary"
            href={manageAccountRouteToUrl({ path: 'manage-account/team' })}
          />
        )}
        <Button
          label="Close"
          size="large"
          variant={showManageTeam ? 'tertiary' : 'primary'}
          href={dashboardRouteToUrl({ path: 'dashboard' })}
        />
      </Rows>
    </Rows>
  );
}

function WrongEmailForm({
  res,
}: {
  res: TeamAcceptInviteResponse & { status: 'wrongEmail' };
}) {
  return (
    <Rows spacing={24}>
      <Rows spacing={16}>
        <Title5>Unable to join team</Title5>
        <TextRegularLight>
          You are signed in as{' '}
          <TextRegular tag="span">{res.yourEmail}</TextRegular> which is not the
          account this invitation was sent to.
        </TextRegularLight>
        <TextRegularLight>
          Please log in to the correct account and try again.
        </TextRegularLight>
      </Rows>
      <Rows spacing={8}>
        <Button
          label="Log in to a different account"
          size="large"
          variant="primary"
          onClick={() => {
            logOutWithoutReload();
            window.location.href = authRouteToUrl({
              path: 'login',
              context: {
                redirect: getCurrentLocationForRedirect(),
                skipPricingTable: true,
              },
            });
          }}
        />
        <CancelButton />
      </Rows>
    </Rows>
  );
}

function AcceptForm({
  token,
  accountsService,
  dryRunRes,
  onFinished,
}: {
  token: string;
  accountsService: AccountsService;
  dryRunRes: TeamAcceptInviteResponse & { status: 'success' };
  onFinished: () => void;
}) {
  const [loading, error, submit] = useSubmit(async () => {
    await accountsService.teamAcceptInvite({ token, dryRun: false });
    onFinished();
    goToDashboard();
  });

  return (
    <Rows spacing={24}>
      <Rows spacing={16}>
        <Title5>Accept Invite</Title5>
        <TextRegularLight>
          You have been invited
          {dryRunRes.type === 'invite' ? (
            <TextRegular tag="span">{' by ' + dryRunRes.invitedBy}</TextRegular>
          ) : (
            ''
          )}
          {' to join '}
          <TextRegular tag="span">{dryRunRes.newTeamName}</TextRegular>.
        </TextRegularLight>
        <OldTeamWarning
          {...dryRunRes.oldTeam}
          newTeamName={dryRunRes.newTeamName}
        />
        <RedErrorText error={error} />
      </Rows>
      <Rows spacing={8}>
        <Button
          label="Accept Invite"
          size="large"
          variant="primary"
          onClick={submit}
          loading={loading}
        />
        <CancelButton />
      </Rows>
    </Rows>
  );
}

function OldTeamWarning({
  name,
  movedProjects,
  newTeamName,
}: {
  name: string;
  movedProjects: boolean;
  newTeamName: string;
}) {
  if (!movedProjects) return null;
  const nameBold = <TextRegular tag="span">{name}</TextRegular>;
  const newTeamBold = <TextRegular tag="span">{newTeamName}</TextRegular>;
  return (
    <TextRegularLight>
      As you are the only remaining member in {nameBold}, existing projects from
      this team will be moved into your private workspace. You can move these
      projects into {newTeamBold} at any time.
    </TextRegularLight>
  );
}

function CancelButton() {
  return (
    <Button label="Cancel" size="large" variant="subtle" href="/dashboard" />
  );
}
